import {
  DEFAULT_STEP_NAMES,
  applyChangeSet,
  bpmnId,
  centre,
  draftChangeSet,
  fromModelerGesture,
  isTaskType,
  layoutMap,
  parseBpmnId,
  stepConfirmation,
  stepTypeOfBpmn,
  toBpmnXml,
  type ChangeContext,
  type ChangeOperation,
  type ChangePreview,
  type EvidenceCitation,
  type MapIssue,
  type MapModel,
  type MapStep,
  type ModelerGesture,
  type StepType,
} from '@caliber-2/discovery-core/maps';
import type { MapCitation, ProcessMapDto } from '@/lib/api/process-maps';

/**
 * Process maps screen logic (T-0189): pure functions, unit-tested.
 *
 * The map engine itself is `@caliber-2/discovery-core/maps`, the same code
 * the API runs (ADR-017): the model, typed change sets, the canvas gesture
 * reader, the lane-grid layout and the BPMN generator. Here: turning API
 * shapes into that model, the bpmn-js markers that stand in for tags (under
 * OQ-V nothing may cover the diagram), and reading what the consultant did
 * on the canvas as change-set operations (Decision 54.32: a drag is a change
 * of lane or order; positions are never kept or sent).
 */

export type { MapModel, ChangeOperation, MapIssue };

// ---------------------------------------------------------------------------
// The model
// ---------------------------------------------------------------------------

/** An engagement map version, as the change-set engine reads it. */
export function modelFromDto(dto: ProcessMapDto): MapModel {
  return {
    lanes: dto.lanes.map((l) => (l.is_system ? { ...l } : { key: l.key, name: l.name })),
    steps: dto.steps.map(
      (s): MapStep => ({
        key: s.key,
        type: s.type,
        lane: s.lane,
        name: s.name,
        system: s.system,
        origin: s.origin,
        origin_user_id: s.origin_user?.id ?? null,
        origin_message_id: s.origin_message_id,
        citations: s.citations.flatMap((c) => citationOf(c)),
        pain_points: s.pain_points.map((p) => ({ pain_point_id: p.id, number: p.number })),
      }),
    ),
    flows: dto.flows.map((f) => ({ ...f })),
  };
}

function citationOf(c: MapCitation): MapStep['citations'] {
  if (c.kind === 'client_validation' && c.validation) {
    return [
      {
        kind: 'client_validation',
        validation_id: c.validation.id,
        validated_by: c.validation.validated_by,
        validated_at: c.validation.validated_at,
      },
    ];
  }
  if (c.kind === 'discovery_evidence' && c.evidence_id) {
    return [
      {
        kind: 'discovery_evidence',
        evidence_id: c.evidence_id,
        evidence_number: c.evidence_number ?? null,
        evidence_status: (c.evidence_status ?? 'confirmed') as EvidenceCitation['evidence_status'],
      },
    ];
  }
  return [];
}

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const asList = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const text = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);

/**
 * A framework's reference process (stored as JSON on the draft or a
 * published version; no citations). Missing fields take the model's
 * defaults; an absent map is an empty one.
 */
export function modelFromReference(json: unknown): MapModel {
  const m = asRecord(json);
  return {
    lanes: asList(m['lanes']).map((l) => ({
      key: text(asRecord(l)['key']),
      name: text(asRecord(l)['name']),
    })),
    steps: asList(m['steps']).map((raw): MapStep => {
      const s = asRecord(raw);
      return {
        key: text(s['key']),
        type: (stepTypeOrTask(s['type']) as StepType) ?? 'task',
        lane: text(s['lane']),
        name: text(s['name']),
        system: typeof s['system'] === 'string' ? s['system'] : null,
        origin: (['ai_draft', 'chat', 'manual', 'canvas'] as const).find((o) => o === s['origin'])
          ? (s['origin'] as MapStep['origin'])
          : 'manual',
        origin_user_id: typeof s['origin_user_id'] === 'string' ? s['origin_user_id'] : null,
        origin_message_id:
          typeof s['origin_message_id'] === 'string' ? s['origin_message_id'] : null,
        citations: [],
        pain_points: [],
      };
    }),
    flows: asList(m['flows']).map((raw, i) => {
      const f = asRecord(raw);
      return {
        key: text(f['key'], `f${i + 1}`),
        from: text(f['from']),
        to: text(f['to']),
        label: typeof f['label'] === 'string' && f['label'] !== '' ? f['label'] : null,
        is_exception: f['is_exception'] === true,
      };
    }),
  };
}

const STEP_TYPES: readonly StepType[] = [
  'start_event',
  'end_event',
  'task',
  'user_task',
  'manual_task',
  'service_task',
  'exclusive_gateway',
];
function stepTypeOrTask(v: unknown): StepType {
  return STEP_TYPES.find((t) => t === v) ?? 'task';
}

export function isEmptyModel(m: MapModel): boolean {
  return m.lanes.length === 0 && m.steps.length === 0 && m.flows.length === 0;
}

/** The pool's name in a BPMN file the API generated: the client's name. */
export function participantName(xml: string): string {
  const m = /<bpmn:participant\b[^>]*\bname="([^"]*)"/.exec(xml);
  if (!m) return 'Client';
  return m[1]!
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

/** BPMN 2.0 for a model, laid out by code exactly as the API lays it out. */
export function bpmnFor(model: MapModel, names: { process: string; participant: string }): string {
  return toBpmnXml(model, layoutMap(model), {
    processName: names.process,
    participantName: names.participant,
  });
}

// ---------------------------------------------------------------------------
// Markers (OQ-V: colour and outline on the shapes, never overlays)
// ---------------------------------------------------------------------------

export type Markers = Record<string, string[]>;

function mark(out: Markers, id: string, cls: string): void {
  (out[id] ??= []).includes(cls) || out[id]!.push(cls);
}

/**
 * What each step shows on the map: unconfirmed (dashed), needs attention
 * (its cited evidence was withdrawn), a pain point (red outline). Reference
 * processes cite nothing, so they show none of these.
 */
export function stepMarkers(model: MapModel, kind: 'engagement' | 'reference'): Markers {
  const out: Markers = {};
  if (kind === 'reference') return out;
  for (const s of model.steps) {
    if (!isTaskType(s.type)) continue;
    const id = bpmnId('step', s.key);
    const c = stepConfirmation(s);
    if (c.confirmation === 'unconfirmed') mark(out, id, 'mk-unconf');
    if (c.needs_attention) mark(out, id, 'mk-attention');
    if (s.pain_points.length > 0) mark(out, id, 'mk-pain');
  }
  return out;
}

/** A proposed change on the map it would leave: added in green, changed in amber. */
export function previewMarkers(preview: ChangePreview): Markers {
  const out: Markers = {};
  for (const k of preview.added) mark(out, bpmnId('step', k), 'mk-added');
  for (const k of [...preview.moved, ...preview.renamed, ...preview.citations_changed])
    mark(out, bpmnId('step', k), 'mk-pending');
  for (const k of preview.flows_changed) mark(out, bpmnId('flow', k), 'mk-pending');
  for (const k of preview.lanes_changed) mark(out, bpmnId('lane', k), 'mk-pending');
  return out;
}

export function mergeMarkers(...all: Markers[]): Markers {
  const out: Markers = {};
  for (const m of all)
    for (const [id, list] of Object.entries(m)) for (const c of list) mark(out, id, c);
  return out;
}

/**
 * The map a proposed change set would leave, for the preview, with what it
 * touches. Null when the engine refuses it here (the server checked it
 * against the same base, so this only happens when the map moved on).
 */
export function proposedMap(
  base: MapModel,
  operations: ReadonlyArray<Record<string, unknown>>,
  context: ChangeContext,
): { model: MapModel; preview: ChangePreview } | null {
  const r = applyChangeSet(base, operations as unknown as ChangeOperation[], context);
  return r.valid ? { model: r.model, preview: r.preview } : null;
}

// ---------------------------------------------------------------------------
// Reading the canvas in edit mode
// ---------------------------------------------------------------------------

/** What the canvas shows after a gesture, read from bpmn-js by the renderer. */
export interface CanvasSnapshot {
  participant: { x: number; y: number; width: number; height: number } | null;
  lanes: Array<{ id: string; name: string; y: number; height: number }>;
  /** Every shape that is not the pool, a lane or a label. */
  shapes: Array<{
    id: string;
    type: string;
    name: string;
    x: number;
    y: number;
    width: number;
    height: number;
  }>;
  connections: Array<{
    id: string;
    type: string;
    source: string | null;
    target: string | null;
    name: string;
  }>;
}

export type EditOutcome =
  /** Nothing the map keeps changed (a resize, the pool dragged): draw the map again. */
  | { kind: 'none' }
  /** Refused: the canvas is put back, with the reason. */
  | { kind: 'refused'; message: string }
  | {
      kind: 'edited';
      /** Every edit since Edit, keys assigned: sent as one change set on Done editing. */
      batch: ChangeOperation[];
      model: MapModel;
      /** What the map still needs before it can be saved. */
      issues: MapIssue[];
      note: string;
    };

type Intent =
  | { kind: 'gesture'; gesture: ModelerGesture; fallback?: ChangeOperation[] }
  | { kind: 'ops'; ops: ChangeOperation[] };

const MOVED = 3;

/**
 * One gesture on the canvas, read against the map being edited (the version
 * plus the edits so far), as change-set operations: through the engine's own
 * gesture reader where it has one (a drop, a palette add, a delete, a
 * connection, a name), so the canvas means what the API means. The map the
 * edits leave is drawn again by code; free positions are never kept.
 */
export function applyCanvasEdit(
  base: MapModel,
  batch: readonly ChangeOperation[],
  canvas: CanvasSnapshot,
  context: ChangeContext,
): EditOutcome {
  const start = draftChangeSet(base, batch, context);
  if (!start.valid) return { kind: 'refused', message: start.message };
  const model = start.model;
  const layout = layoutMap(model);

  const unsupported = canvas.shapes.find((s) => stepTypeOfBpmn(s.type) === null);
  if (unsupported)
    return {
      kind: 'refused',
      message: 'Only steps, decisions, starts and ends are kept on the map.',
    };
  if (canvas.connections.some((c) => c.type !== 'bpmn:SequenceFlow'))
    return { kind: 'refused', message: 'Only flows between steps are kept on the map.' };
  // Adding or deleting a lane makes bpmn-js shift the pool, the lanes and the
  // steps under them to make or close space. That is not a drag: only the
  // lane change is read, and the map is laid out again.
  const laneIds = new Set(canvas.lanes.map((l) => l.id));
  const lanesChanged =
    canvas.lanes.some((l) => {
      const p = parseBpmnId(l.id);
      return !(p?.kind === 'lane' && model.lanes.some((x) => x.key === p.key));
    }) || model.lanes.some((l) => !laneIds.has(bpmnId('lane', l.key)));
  const pool = canvas.participant;
  if (
    !lanesChanged &&
    pool &&
    (Math.abs(pool.x - layout.pool.x) > MOVED || Math.abs(pool.y - layout.pool.y) > MOVED)
  )
    return { kind: 'none' };

  const steps = new Map(model.steps.map((s) => [s.key, s]));
  const flows = new Map(model.flows.map((f) => [f.key, f]));
  const lanes = new Map(model.lanes.map((l) => [l.key, l]));
  const stepKeyOf = (id: string | null): string | null => {
    const p = parseBpmnId(id);
    return p?.kind === 'step' && steps.has(p.key) ? p.key : null;
  };

  const intents: Intent[] = [];
  const notes: string[] = [];

  // Lanes added (bpmn-js "add lane above / below"), above the lane now below them.
  const lanesByY = [...canvas.lanes].sort((a, b) => a.y - b.y);
  const takenLanes = new Set(model.lanes.map((l) => l.key));
  const newLaneKey = new Map<string, string>();
  lanesByY.forEach((l, i) => {
    const p = parseBpmnId(l.id);
    if (p?.kind === 'lane' && lanes.has(p.key)) return;
    const key = freeKey('l', takenLanes);
    newLaneKey.set(l.id, key);
    const below = lanesByY
      .slice(i + 1)
      .map((x) => parseBpmnId(x.id))
      .find((x) => x?.kind === 'lane' && lanes.has(x.key));
    const name = l.name.trim() || 'New lane';
    intents.push({
      kind: 'ops',
      ops: [{ op: 'add_lane', key, name, ...(below ? { before_lane: below.key } : {}) }],
    });
    notes.push(`Added the lane “${name}”. Double-click its name to rename it.`);
  });
  const laneAt = (y: number): string | null => {
    const hit = canvas.lanes.find((l) => y >= l.y && y < l.y + l.height);
    if (!hit) return null;
    const p = parseBpmnId(hit.id);
    return p?.kind === 'lane' && lanes.has(p.key) ? p.key : (newLaneKey.get(hit.id) ?? null);
  };

  // Steps added: from the palette (placed after the step to their left, as the
  // API reads a drop), or appended from another step with its flow.
  const takenSteps = new Set(model.steps.map((s) => s.key));
  const newStepKey = new Map<string, string>();
  const added = canvas.shapes.filter((s) => !stepKeyOf(s.id));
  for (const s of added) newStepKey.set(s.id, freeKey('s', takenSteps));
  const endOf = (id: string | null): string | null =>
    stepKeyOf(id) ?? (id ? (newStepKey.get(id) ?? null) : null);
  const newFlows = canvas.connections.filter((c) => {
    const p = parseBpmnId(c.id);
    return !(p?.kind === 'flow' && flows.has(p.key));
  });
  for (const s of added) {
    const type = stepTypeOfBpmn(s.type)!;
    const key = newStepKey.get(s.id)!;
    const name = s.name.trim() || DEFAULT_STEP_NAMES[type];
    const c = { x: s.x + s.width / 2, y: s.y + s.height / 2 };
    const lane = laneAt(c.y);
    if (!lane) return { kind: 'refused', message: 'Drop steps inside a lane.' };
    const wired = newFlows.some((f) => f.source === s.id || f.target === s.id);
    const plain: ChangeOperation = { op: 'add_step', key, type, lane, name };
    if (wired || newLaneKey.size > 0) {
      intents.push({ kind: 'ops', ops: [plain] });
    } else {
      intents.push({
        kind: 'gesture',
        gesture: { kind: 'create', element_type: s.type, x: c.x, y: c.y, name },
        fallback: [plain],
      });
    }
  }

  // Flows drawn, and flows re-pointed at another step.
  for (const c of newFlows) {
    const from = endOf(c.source);
    const to = endOf(c.target);
    if (!from || !to) continue;
    const label = c.name.trim();
    intents.push({ kind: 'ops', ops: [{ op: 'add_flow', from, to, ...(label ? { label } : {}) }] });
  }
  for (const c of canvas.connections) {
    const p = parseBpmnId(c.id);
    const f = p?.kind === 'flow' ? flows.get(p.key) : undefined;
    if (!f) continue;
    const from = endOf(c.source);
    const to = endOf(c.target);
    if (!from || !to || (from === f.from && to === f.to)) continue;
    intents.push({
      kind: 'ops',
      ops: [
        { op: 'remove_flow', flow: f.key },
        { op: 'add_flow', from, to, label: f.label, is_exception: f.is_exception },
      ],
    });
  }

  // Steps dragged: a change of lane and of order, read by the engine.
  const moved = canvas.shapes
    .filter((s) => {
      const key = stepKeyOf(s.id);
      if (!key || lanesChanged) return false;
      const was = centre(layout.steps.get(key)!);
      return (
        Math.abs(s.x + s.width / 2 - was.x) > MOVED || Math.abs(s.y + s.height / 2 - was.y) > MOVED
      );
    })
    .sort((a, b) => a.x - b.x);
  for (const s of moved) {
    intents.push({
      kind: 'gesture',
      gesture: { kind: 'move', element: s.id, x: s.x + s.width / 2, y: s.y + s.height / 2 },
    });
  }

  // Names typed on steps, lanes and flows.
  for (const s of canvas.shapes) {
    const key = stepKeyOf(s.id);
    if (key && s.name !== steps.get(key)!.name)
      intents.push({ kind: 'gesture', gesture: { kind: 'label', element: s.id, text: s.name } });
  }
  for (const l of canvas.lanes) {
    const p = parseBpmnId(l.id);
    const lane = p?.kind === 'lane' ? lanes.get(p.key) : undefined;
    if (lane && l.name !== lane.name)
      intents.push({ kind: 'gesture', gesture: { kind: 'label', element: l.id, text: l.name } });
  }
  for (const c of canvas.connections) {
    const p = parseBpmnId(c.id);
    const f = p?.kind === 'flow' ? flows.get(p.key) : undefined;
    if (f && c.name !== (f.label ?? ''))
      intents.push({ kind: 'gesture', gesture: { kind: 'label', element: c.id, text: c.name } });
  }

  // Deleted: steps (with their flows), flows and lanes, as one gesture.
  const onCanvas = new Set([
    ...canvas.shapes.map((s) => s.id),
    ...canvas.connections.map((c) => c.id),
    ...canvas.lanes.map((l) => l.id),
  ]);
  const gone = [
    ...model.steps.map((s) => bpmnId('step', s.key)),
    ...model.flows.map((f) => bpmnId('flow', f.key)),
    ...model.lanes.map((l) => bpmnId('lane', l.key)),
  ].filter((id) => !onCanvas.has(id));
  if (gone.length > 0)
    intents.push({ kind: 'gesture', gesture: { kind: 'delete', elements: gone } });

  if (intents.length === 0) return { kind: 'none' };

  // Fold the intents into the batch, each read against the map the ones
  // before it leave, and keep the map they all leave.
  let next = [...batch];
  let current = model;
  let issues: MapIssue[] = start.issues;
  for (const intent of intents) {
    let ops: ChangeOperation[];
    if (intent.kind === 'gesture') {
      const read = fromModelerGesture(current, intent.gesture);
      if (!read.valid) return { kind: 'refused', message: read.message };
      ops = read.operations;
    } else ops = intent.ops;
    if (ops.length === 0) continue;
    let tried = draftChangeSet(base, [...next, ...ops], context);
    if (!tried.valid && intent.kind === 'gesture' && intent.fallback) {
      // A drop after a decision with two ways out: keep the step where it was
      // dropped, unconnected, for the consultant to connect.
      tried = draftChangeSet(base, [...next, ...intent.fallback], context);
    }
    if (!tried.valid) return { kind: 'refused', message: tried.message };
    next = tried.operations;
    current = tried.model;
    issues = tried.issues;
  }
  if (next.length === batch.length) return { kind: 'none' };
  return {
    kind: 'edited',
    batch: next,
    model: current,
    issues,
    note: notes[0] ?? describeEdit(model, current, next.slice(batch.length)),
  };
}

function freeKey(prefix: string, taken: Set<string>): string {
  for (let n = 1; ; n += 1) {
    const key = `${prefix}${n}`;
    if (!taken.has(key)) {
      taken.add(key);
      return key;
    }
  }
}

const q = (s: string) => `“${s}”`;

/** What one gesture did, in the prototype's words, for the line above the canvas. */
export function describeEdit(before: MapModel, after: MapModel, ops: ChangeOperation[]): string {
  const step = (m: MapModel, key: string) => m.steps.find((s) => s.key === key);
  const laneName = (m: MapModel, key: string) => m.lanes.find((l) => l.key === key)?.name ?? key;
  const first = ops[0];
  if (!first) return '';
  switch (first.op) {
    case 'add_step': {
      const s = step(after, first.key ?? '');
      const name = s?.name ?? first.name;
      if (first.after_step) {
        const prev = step(after, first.after_step);
        return `Added ${q(name)} in ${laneName(after, first.lane)}, after ${q(prev?.name ?? first.after_step)}. Double-click it to rename it; it stays unconfirmed until evidence is linked.`;
      }
      const connected = ops.some((o) => o.op === 'add_flow');
      return connected
        ? `Added ${q(name)} in ${laneName(after, first.lane)}, connected. It stays unconfirmed until evidence is linked.`
        : `Added ${q(name)} in ${laneName(after, first.lane)}. Connect it and rename it; it stays unconfirmed until evidence is linked.`;
    }
    case 'move_step': {
      const s = step(after, first.step);
      const column = (layoutMap(after).steps.get(first.step)?.column ?? 0) + 1;
      return `${q(s?.name ?? first.step)} is now in ${laneName(after, s?.lane ?? '')}, position ${column} across. Laid out again by code; positions are not kept.`;
    }
    case 'remove_step':
      return `Removed ${q(step(before, first.step)?.name ?? first.step)}.`;
    case 'remove_flow':
      return ops[1]?.op === 'add_flow' ? 'Re-pointed the flow.' : 'Removed the flow.';
    case 'remove_lane':
      return `Removed the lane ${q(laneName(before, first.lane))}.`;
    case 'add_flow':
      return `Connected ${q(step(after, first.from)?.name ?? first.from)} to ${q(step(after, first.to)?.name ?? first.to)}.`;
    case 'rename_step':
    case 'rename_lane':
      return `Renamed to ${q(first.name.trim())}.`;
    case 'relabel_flow':
      return first.label ? `Labelled the flow ${q(first.label)}.` : 'Removed the flow’s label.';
    default:
      return 'Changed the map.';
  }
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** How the step panel names a step's type (prototype `TYPE_LABEL`). */
export const STEP_TYPE_LABEL: Record<StepType, string> = {
  task: 'Step',
  user_task: 'A person, in a system',
  manual_task: 'A person, on paper or in a spreadsheet',
  service_task: 'The system, automatically',
  start_event: 'Start',
  end_event: 'End',
  exclusive_gateway: 'Decision',
};

/** `5 Oct 2026` from an ISO time. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

/** The message for a refused change set or write, in plain words. */
export function refusalText(reason: string | undefined | null): string {
  const r = (reason ?? '').trim().replace(/\.$/, '');
  return r
    ? `I can't make that change: ${lowerFirst(r)}. Nothing changed.`
    : "I can't make that change. Nothing changed.";
}

function lowerFirst(s: string): string {
  return s.length > 1 && /^[A-Z][a-z]/.test(s) ? s[0]!.toLowerCase() + s.slice(1) : s;
}

/** Undo's words: it writes a new version with an earlier one's content (Decision 54.28). */
export function undoLabel(latest: number): string | null {
  return latest > 1 ? `Undo the last change: writes v${latest + 1} restoring v${latest - 1}` : null;
}

/**
 * Things to try, named from the map itself (prototype `chatPanel()`
 * suggestions): a move, an add, a question code answers.
 */
export function chatSuggestions(model: MapModel, kind: 'engagement' | 'reference'): string[] {
  const out: string[] = [];
  const tasks = model.steps.filter((s) => isTaskType(s.type) && s.name);
  const mover = tasks.find((s) => model.lanes.some((l) => l.key !== s.lane));
  const lane = mover ? model.lanes.find((l) => l.key !== mover.lane) : undefined;
  if (mover && lane) out.push(`Move ${q(mover.name)} to the ${lane.name} lane`);
  const start = model.steps.find((s) => s.type === 'start_event' && s.name);
  if (start) out.push(`Add ${q('Check the details')} after ${q(start.name)}`);
  const labelled = model.flows.find((f) => f.label);
  const from = labelled && model.steps.find((s) => s.key === labelled.from);
  const to = labelled && model.steps.find((s) => s.key === labelled.to);
  if (from?.name && to?.name) out.push(`Remove the flow from ${q(from.name)} to ${q(to.name)}`);
  out.push('Which steps are still on paper?');
  if (kind === 'engagement') out.push('Which steps are unconfirmed?');
  return out;
}
